import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Clock } from 'lucide-react';

const RideSchedulePage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'pickup' | 'dropoff'>('pickup');

  const now = new Date();
  const gmtNow = new Date(now.toUTCString());
  const roundedGmtNow = new Date(gmtNow);
  roundedGmtNow.setUTCMinutes(Math.ceil(gmtNow.getUTCMinutes() / 5) * 5, 0, 0);

  const dates = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => {
        const d = new Date(gmtNow);
        d.setUTCDate(d.getUTCDate() + i);
        return d;
      }),
    [gmtNow],
  );

  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [selectedTime, setSelectedTime] = useState(
    `${String(roundedGmtNow.getUTCHours()).padStart(2, '0')}:${String(roundedGmtNow.getUTCMinutes()).padStart(2, '0')}`,
  );

  const formatDate = (d: Date) =>
    new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: 'Africa/Banjul',
    }).format(d);

  const formattedTime = () => {
    const [h, m] = selectedTime.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
  };

  const handleContinue = () => {
    const chosenDate = dates[selectedDateIndex];
    const [h, m] = selectedTime.split(':').map(Number);

    const scheduledAt = new Date(
      Date.UTC(chosenDate.getUTCFullYear(), chosenDate.getUTCMonth(), chosenDate.getUTCDate(), h, m, 0, 0),
    );

    navigate('/ride', {
      state: {
        openBooking: true,
        scheduledAtISO: scheduledAt.toISOString(),
      },
    });
  };

  return (
    <div className="min-h-screen bg-white safe-area-top">
      <div className="flex items-center gap-3 px-4 pt-12 pb-4 border-b border-gray-100">
        <button onClick={() => navigate('/ride')} className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-gray-100">
          <ChevronLeft className="h-5 w-5 text-gray-800" />
        </button>
      </div>

      <div className="px-6 pb-36">
        <h1 className="text-2xl font-bold text-gray-900 mt-4 mb-6">Choose a time</h1>

        <div className="flex bg-gray-100 rounded-full p-1 mb-6">
          <button
            onClick={() => setTab('pickup')}
            className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition-all ${
              tab === 'pickup' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
            }`}
          >
            Pickup at
          </button>
          <button
            onClick={() => setTab('dropoff')}
            className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition-all ${
              tab === 'dropoff' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
            }`}
          >
            Dropoff by
          </button>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-3 mb-4 -mx-2 px-2">
          {dates.map((d, i) => (
            <button
              key={i}
              onClick={() => setSelectedDateIndex(i)}
              className={`flex-shrink-0 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                selectedDateIndex === i ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {i === 0 ? 'Today' : formatDate(d)}
            </button>
          ))}
        </div>

        <p className="text-center text-lg font-medium text-gray-900 mb-5">{formatDate(dates[selectedDateIndex])}</p>

        <div className="border-t border-gray-100 mb-6" />

        <div className="rounded-2xl border border-gray-200 p-4 mb-6">
          <label className="block text-sm font-semibold text-gray-900 mb-3">Time (GMT)</label>
          <div className="relative">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="time"
              step={300}
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              className="w-full h-12 pl-11 pr-4 rounded-xl border border-gray-200 bg-white text-gray-900 font-semibold outline-none"
            />
          </div>
        </div>

        <p className="text-center font-semibold text-gray-900 mb-1">{formattedTime()} GMT {tab === 'pickup' ? 'pickup time' : 'dropoff time'}</p>
        <p className="text-center text-sm text-gray-500">About 10 min ride</p>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-6 pb-8 pt-4">
        <p className="text-xs text-gray-500 mb-4">
          Standard Reserve Cancellation Policy: No cancel fee up to an hour before pickup or anytime before your driver is assigned.
        </p>
        <Button onClick={handleContinue} className="w-full h-12 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl text-base">
          Continue
        </Button>
      </div>
    </div>
  );
};

export default RideSchedulePage;
